import {
  Control,
  Controller,
  FieldError,
  FieldErrors,
  RegisterOptions,
} from "react-hook-form";
import { ErrorMessage } from "./ErrorMessage";
import { useMemo, useRef } from "react";
import { NoxusFormLabel } from "./NoxusFormLabel";
import { NoxusAlert } from "../UI/NoxusAlert";
import {
  DollarSign,
  Gauge,
  AlignJustify,
  Megaphone,
  UsersRound,
  Calculator,
  Laptop,
  Scale,
  Newspaper,
  Globe,
  AlignLeft,
  AudioLines,
  ExternalLink,
  FileText,
  Image,
  Building2,
  HeartPulse,
  Phone,
  ShoppingCart,
  Landmark,
  Hospital,
  RadioTower,
  Store,
  Box,
  Settings2,
  SlidersHorizontal,
  Sparkles,
  Target,
  type LucideIcon,
} from "lucide-react";
import { Skeleton } from "../shadcn/skeleton";
import { ModelDefinition } from "@/generated";
import { cn } from "@/lib/utils";
import { Button } from "@/components/shadcn/button";
import { Badge } from "../shadcn/badge";
import { useModels } from "@/hooks/useModels";

export interface OptionType {
  value: string;
  label: string;
  customFields?: any;
}

interface IHorizontalSelectProps {
  label?: string;
  helperText?: string;
  options: OptionType[];
  value?: string | string[];
  tooltip?: string;
  component?: { type: any; params: any };
  onChange: (value: string | string[]) => void;
  isDisabled?: boolean;
  isMulti?: boolean;
  canWrap?: boolean;
  name?: string;
}

export const HorizontalSelect = (props: IHorizontalSelectProps) => {
  const tagRefs = useRef<(HTMLSpanElement | null)[]>([]);
  const isDisabled = props.isDisabled;

  const handleClick = (clickedValue: string) => {
    if (props.isDisabled) return;
    if (props.isMulti) {
      const currentValues = Array.isArray(props.value)
        ? (props.value as string[])
        : [];
      const isSelected = currentValues.includes(clickedValue);
      const nextValues = isSelected
        ? currentValues.filter((v) => v !== clickedValue)
        : [...currentValues, clickedValue];
      props.onChange(nextValues);
    } else {
      props.onChange(clickedValue);
    }
  };

  const Component = props.component?.type;

  const handleKeyDown = (event: React.KeyboardEvent) => {
    const focusedElement = document.activeElement;
    const currentIndex = tagRefs.current.findIndex(
      (ref) => ref === focusedElement
    );

    if (event.key === "ArrowDown" || event.key === "ArrowRight") {
      // Move focus down the list

      if (currentIndex >= 0 && currentIndex < tagRefs.current.length - 1) {
        tagRefs.current[currentIndex + 1]?.focus();
        event.preventDefault();
      }
    } else if (event.key === "ArrowUp" || event.key === "ArrowLeft") {
      // Move focus up the list

      if (currentIndex > 0) {
        tagRefs.current[currentIndex - 1]?.focus();
        event.preventDefault();
      }
    }
  };

  const getIconComponent = (icon: string) => {
    return iconMap[icon as keyof typeof iconMap];
  };

  return (
    <div className="w-full">
      {props.label && (
        <NoxusFormLabel
          label={props.label}
          tooltip={props.tooltip}
          name={props.name}
          noMargin={!!props.helperText}
        />
      )}

      {props.helperText && (
        <p className="text-xs text-muted-foreground mb-3 mt-1">
          {props.helperText}
        </p>
      )}

      <div
        className={cn(
          "flex gap-4 w-full",
          props.canWrap ? "flex-wrap" : "flex-nowrap",
          isDisabled
            ? "opacity-50 pointer-events-none cursor-not-allowed"
            : "opacity-100 pointer-events-auto cursor-pointer"
        )}
        onKeyDown={handleKeyDown}
      >
        {props.options.map((option, index) => {
          const isSelectedOption = props.isMulti
            ? props.value?.includes(option.value)
            : props.value === option.value;

          const IconComponent = option.customFields?.icon
            ? getIconComponent(option.customFields.icon)
            : undefined;

          return Component ? (
            <Component
              componentRef={(el: HTMLSpanElement | null) =>
                (tagRefs.current[index] = el)
              }
              key={`select-component-option-${option.value}`}
              index={index}
              {...props.component?.params}
              option={{
                value: option.value,
                label: option.label,
                ...option.customFields,
              }}
              isDisabled={isDisabled}
              isSelectedOption={isSelectedOption}
              onClick={() => handleClick(option.value)}
            />
          ) : (
            <Badge
              key={`select-component-option-${option.value}`}
              className="py-1 px-3 text-sm"
              variant={isSelectedOption ? "default" : "outline"}
              onClick={() => handleClick(option.value)}
            >
              {IconComponent && (
                <IconComponent className="h-4 w-4" strokeWidth={1.33} />
              )}
              {option.label}
            </Badge>
          );
        })}
      </div>
    </div>
  );
};

interface IHorizontalSelectFieldProps {
  name: string;
  tooltip?: string;
  label?: string;
  helperText?: string;
  component?: { type: string; params: Record<string, unknown> };
  rules?: RegisterOptions;
  errors?: FieldErrors;
  control: Control<any>;
  options: {
    value: string;
    label: string;
    customFields?: Record<string, unknown>;
  }[];
  defaultValue?: string | string[];
  onChange?: (value: string | string[]) => void;
  isMulti?: boolean;
  endpoint?: string;
  isDisabled?: boolean;
  canWrap?: boolean;
}

export const HorizontalSelectField = (props: IHorizontalSelectFieldProps) => {
  const hasError = !!(props.errors && props.errors[props.name]);

  const {
    data: modelsData,
    isLoading,
    isError,
  } = useModels(props.endpoint ?? "", !!props.endpoint);

  const modelsUpdatedData: ModelDefinition[] = useMemo(() => {
    if (!modelsData) {
      return [];
    }

    const visibleModels = modelsData.filter(
      (model) => model.visible !== false && model.is_active
    );
    return visibleModels || [];
  }, [modelsData]);

  // Skeleton stays up until react-query has actually returned a value.
  // ``isLoading`` flips false for a tick before the fetch resolves on
  // the very first render (and after ``enabled`` toggles on), so gating
  // the empty-state on ``isLoading`` alone flashes "No models
  // configured" between mount and the first response.
  const stillFetching =
    !!props.endpoint && (isLoading || modelsData === undefined);
  if (stillFetching) {
    return <Skeleton className="h-5 w-full" />;
  }

  if (modelsUpdatedData.length === 0 && !!props.endpoint) {
    return (
      <NoxusAlert
        variant="warning"
        size="xs"
        title="No models configured"
        description="You have no models configured. Add them in the Models page in Settings."
        action={{
          label: (
            <>
              <ExternalLink size={12} />
              Go to Models
            </>
          ),
          onClick: () =>
            window.open("/settings/platform/models", "_blank", "noopener"),
        }}
      />
    );
  }

  return (
    <div className="flex flex-col w-full gap-1 items-start">
      <Controller
        control={props.control}
        disabled={props.isDisabled}
        defaultValue={props.defaultValue}
        name={props.name}
        rules={props.rules}
        render={({ field: { onChange, value } }) => (
          <HorizontalSelect
            value={value}
            label={props.label}
            helperText={props.helperText}
            isDisabled={props.isDisabled}
            isMulti={props.isMulti}
            canWrap={props.canWrap}
            tooltip={props.tooltip}
            options={props.options}
            onChange={(value) => {
              onChange(value);
              if (props.onChange) props.onChange(value);
            }}
            component={props.component}
            name={props.name}
          />
        )}
      />
      {hasError && (
        <ErrorMessage error={props.errors![props.name] as FieldError} />
      )}
    </div>
  );
};

export const iconMap: Record<string, LucideIcon> = {
  image: Image,
  box: Box,
  audio: AudioLines,
  building: Building2,
  file: FileText,
  str: AlignLeft,
  gauge: Gauge,
  dollar: DollarSign,
  text: AlignJustify,
  Finance: Calculator,
  HR: UsersRound,
  Sales: DollarSign,
  ["Software Development"]: Laptop,
  Legal: Scale,
  ["Business and Marketing"]: Megaphone,
  ["Media / News"]: Newspaper,
  ["Web Scraping"]: Globe,
  hospital: Hospital,
  landmark: Landmark,
  radiotower: RadioTower,
  phone: Phone,
  store: Store,
  sparkles: Sparkles,
  target: Target,
  settings2: Settings2,
  "sliders-horizontal": SlidersHorizontal,
};
